/**
 * The route explorer.
 *
 * An app-level control rather than a tool panel, because a route belongs to
 * the whole fab and to none of the tools in it. Everything else in this
 * application is organised the way a floor plan is - here is a machine, walk up
 * to it. This is the one view organised the way a fab actually works: here is a
 * product, and here are the three hundred operations it takes to build it.
 *
 * Two things are on screen that cannot be seen anywhere else:
 *
 *   THE FLAT LIST   "step 47 of 135", with the tool for each one. It is how
 *                   work is scheduled and how a technician is told what to do,
 *                   and until you have seen one it is easy to imagine a fab is
 *                   a line of tools rather than a loop run over and over.
 *
 *   THE COMPARISON  four ways to reach the same pitch, priced per good die.
 *                   The answer is close, which is not a modelling artefact -
 *                   it is why the industry argued about it for a decade.
 *
 * React edition of ui/RouteExplorer.ts, the last vanilla surface. Every class
 * name is kept, so the existing stylesheet carries the look unchanged; the
 * imperative paint()/buildNav() pair is gone, because a view that is a pure
 * function of (view, selection, knobs) is exactly what a render is.
 */

import { Slider } from 'antd'
import { Fragment, useState, type ReactNode } from 'react'
import { t, tOr } from '../../i18n/i18n'
import type { Journal } from '../../learn/Journal'
import { missingForTags } from '../../learn/floor'
import {
  DEFAULT_ROUTE_OPTIONS,
  costByTool,
  costPerGoodDie,
  expand,
  runRoute,
  visitCounts,
  type ProcessFlow,
  type RouteOptions,
} from '../../sim/flow/model'
import {
  BACK_END,
  METAL_LAYER,
  PATTERNING_ROUTES,
  QUANTUM_CHIP,
  SAQP,
  SUBSTRATE,
  gateModule,
  productRoute,
} from '../../sim/flow/routes'
import {
  CMP_ROUTE_DERIVED_SPEC_NM,
  CMP_VISIT_SPEC_NM,
  NOMINAL_PLANARISATION_EFFICIENCY,
  ROUTE_FAULTS,
  driftAcrossLayers,
  planarisationStack,
  thermalAccount,
} from '../../sim/flow/routeFaults'
import { CD_BUDGET_NM } from '../../sim/litho/topography'
import {
  ROUTE_CONDITION_MODEL,
  type RouteAspect,
  type RouteCondition,
} from '../../sim/flow/routeCondition'
import { ShiftState } from '../../tools/ShiftState'
import { Note } from '../../ui/kit/primitives'
import { ShiftReportPane } from '../../ui/kit/ShiftReportPane'
import { useLang } from '../i18n/LanguageProvider'



const CATALOGUE: Array<{ group: string; groupId: string; flows: ProcessFlow[] }> = [
  { group: 'Patterning modules', groupId: 'patterning', flows: PATTERNING_ROUTES },
  { group: 'Layer modules', groupId: 'layers', flows: [gateModule(SAQP), METAL_LAYER] },
  { group: 'Product', groupId: 'product', flows: [SUBSTRATE, BACK_END, productRoute(SAQP, 10)] },
  { group: 'Quantum devices', groupId: 'quantum', flows: [QUANTUM_CHIP] },
]

const usd = (v: number) => '$' + (v >= 100 ? v.toFixed(0) : v.toFixed(2))

const stepSlug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')

/** One line of the flat step list. */
function StepRow({
  index,
  total,
  tool,
  label,
  purpose,
  mask,
}: {
  index: number
  total: number
  tool: string
  label: string
  purpose: string
  mask?: boolean
}) {
  return (
    <div className="route-step">
      <span className="route-n">{String(index + 1).padStart(String(total).length, '0')}</span>
      <span
        className={(mask ? 'route-tool mask' : 'route-tool') + (missingForTags([tool]).length ? ' missing' : '')}
        title={missingForTags([tool]).length ? t('author.NotOnFloor', { tags: tool }) : undefined}
      >
        {tool}
      </span>
      <span className="route-label">{tOr(`tool.route.stepLabel.${stepSlug(label)}`, label)}</span>
      <span className="route-purpose">
        {tOr(`tool.route.stepPurpose.${stepSlug(purpose)}`, purpose)}
      </span>
    </div>
  )
}

function RouteView({ flow, options }: { flow: ProcessFlow; options: RouteOptions }) {
  useLang()
  const r = runRoute(flow, options)
  const perDie = costPerGoodDie(flow, options)

  // The numbers that belong to the route and to no tool in it.
  const tr = (k: string, fallback: string) => tOr(`tool.route.${k}`, fallback)
  const tiles: Array<[string, string, string]> = [
    [tr('TileOperations', 'Operations'), String(r.stepCount), tr('TileOperationsNote', 'the wafer stops this many times')],
    [tr('ThMasks', 'Masks'), String(r.maskCount), tr('TileMasksNote', 'each one is a reticle, bought once')],
    [tr('TileCost', 'Cost'), usd(r.costUsd) + t('tool.route.PerWaferSuffix'), usd(r.reticleCostUsd) + ' ' + tr('TileCostNote', 'of it reticle')],
    [tr('TileLithoShare', 'Litho share'), (100 * r.lithoShare).toFixed(0) + '%', t('tool.route.TileLithoNote', { masks: r.maskCount, steps: r.stepCount })],
    [tr('TileDefects', 'Defects added'), '+' + r.addedDefectsCm2.toFixed(4) + '/cm²', tr('TileDefectsNote', 'every touch costs something')],
    [tr('TileThermal', 'Thermal budget'), r.thermalSeconds.toFixed(0) + ' s', tr('TileThermalNote', 'equivalent seconds at 1000 °C')],
    [tr('TileLineYield', 'Line yield'), (100 * r.lineYield).toFixed(2) + '%', tr('TileLineYieldNote', 'whole wafers that survive')],
    [tr('TileDieYield', 'Die yield'), (100 * r.dieYield).toFixed(2) + '%', t('tool.route.TileDieYieldNote', { area: options.dieAreaCm2 })],
    [tr('TilePerGoodDie', 'Per good die'), '$' + perDie.usdPerGoodDie.toFixed(4), t('tool.route.TileGoodDieNote', { good: perDie.goodDie.toFixed(0), gross: perDie.grossDie })],
  ]

  // Where the wafer goes, and where the money goes. Different answers, and
  // that difference is most of what a route teaches.
  const visits = visitCounts(flow).slice(0, 6)
  const costs = costByTool(flow).slice(0, 6)

  // The flat list. Capped, because a product route is long and the point is
  // made by the first thirty and the count.
  const steps = expand(flow)
  const shown = steps.slice(0, 40)

  return (
    <div>
      <div className="route-purposeline">
        {tOr(`tool.route.flow.${flow.id}.Purpose`, flow.purpose)}
      </div>

      <div className="route-tiles">
        {tiles.map(([k, v, n]) => (
          <div className="route-tile" key={k}>
            <span className="k">{k}</span>
            <span className="v">{v}</span>
            <span className="n">{n}</span>
          </div>
        ))}
      </div>

      <div className="route-two">
        <div>
          <h4>{tOr('tool.route.TextMostVisited', 'Most visited')}</h4>
          {visits.map((v) => (
            <div className="route-bar" key={v.tool}>
              <span className="route-tool">{v.tool}</span>
              <span
                className="route-fill"
                style={{ width: `${((100 * v.visits) / visits[0].visits).toFixed(1)}%` }}
              />
              <span className="route-num">{'×' + v.visits}</span>
            </div>
          ))}
        </div>
        <div>
          <h4>{tOr('tool.route.TextMostExpensive', 'Most expensive')}</h4>
          {costs.map((c) => (
            <div className="route-bar" key={c.tool}>
              <span className="route-tool">{c.tool}</span>
              <span
                className="route-fill cost"
                style={{ width: `${((100 * c.usd) / costs[0].usd).toFixed(1)}%` }}
              />
              <span className="route-num">{usd(c.usd)}</span>
            </div>
          ))}
        </div>
      </div>

      <h4 className="route-h">
        {t('tool.route.StepListHead', { shown: shown.length, total: steps.length })}
      </h4>
      <div className="route-list">
        {shown.map((s, i) => (
          <StepRow
            key={i}
            index={i}
            total={steps.length}
            tool={s.tool}
            label={s.label}
            purpose={s.purpose}
            mask={s.mask}
          />
        ))}
        {steps.length > shown.length && (
          <div className="route-more">
            {t('tool.route.StepListMore', { more: steps.length - shown.length })}
          </div>
        )}
      </div>
    </div>
  )
}

/** The patterning comparison, which is the reason the abstraction exists. */
function ComparisonView({ options }: { options: RouteOptions }) {
  useLang()
  const rows = PATTERNING_ROUTES.map((f) => ({ f, r: runRoute(f, options), d: costPerGoodDie(f, options) }))
  const best = Math.min(...rows.map((x) => x.d.usdPerGoodDie))

  const HEADERS: Array<[string, string]> = [
    ['Route', 'ThRoute'], ['Steps', 'ThSteps'], ['Masks', 'ThMasks'], ['$/wafer', 'ThPerWafer'],
    ['Litho', 'ThLitho'], ['Composite yield', 'ThYield'], ['$/good die', 'ThPerGoodDie'],
  ]

  return (
    <div>
      <div className="route-purposeline">
        {tOr(
          'tool.route.CompareIntro',
          'Five ways to put a pattern on a wafer. The last three reach a pitch no single 193 nm ' +
            'exposure can resolve, and they get there by trading masks against operations — a ' +
            'mask is bought once and a step is paid for on every wafer. Change the die size or ' +
            'the production volume below and the ranking moves, which is the real answer: there ' +
            'is no best route, only a best route for a product.',
        )}
      </div>
      <div className="route-compare">
        {HEADERS.map(([h, key]) => (
          <span className="route-th" key={key}>
            {tOr(`tool.route.${key}`, h)}
          </span>
        ))}
        {rows.map(({ f, r, d }) => {
          const winner = Math.abs(d.usdPerGoodDie - best) < 1e-9
          const cls = winner ? 'route-td win' : 'route-td'
          return (
            <Fragment key={f.id}>
              <span className={cls}>{tOr(`tool.route.flow.${f.id}.Name`, f.name)}</span>
              <span className={cls}>{String(r.stepCount)}</span>
              <span className={cls}>{String(r.maskCount)}</span>
              <span className={cls}>{usd(r.costUsd)}</span>
              <span className={cls}>{(100 * r.lithoShare).toFixed(0) + '%'}</span>
              <span className={cls}>{(100 * r.compositeYield).toFixed(2) + '%'}</span>
              <span className={cls}>{'$' + d.usdPerGoodDie.toFixed(4)}</span>
            </Fragment>
          )
        })}
      </div>
    </div>
  )
}

interface SeriesOpts {
  visitLimit?: number
  routeLimit?: number
  unit: string
  labels?: string[]
  /**
   * Draw deviations from a centreline rather than bars from zero.
   *
   * A control chart is a signed series and has to be drawn as one. Shifting
   * the values up to make them positive would put numbers on the limit
   * labels that are not the numbers anybody works with.
   */
  centre?: number
}

/**
 * A control chart: deviations either side of a centreline, with the spec and
 * the control limits drawn as symmetric pairs.
 *
 * Separate from the bar renderer because the question is different. A bar
 * chart asks how big; a control chart asks which side, and for how long.
 */
function centredSeries(values: number[], opts: SeriesOpts & { centre: number }): ReactNode {
  const span =
    Math.max(
      ...values.map((v) => Math.abs(v - opts.centre)),
      opts.visitLimit ?? 0,
      opts.routeLimit ?? 0,
    ) * 1.15
  // Position as a percentage of the plot height, with the centreline at 50%.
  const pos = (deviation: number) => 50 + (50 * deviation) / span

  const limits: Array<[number | undefined, string, string]> = [
    [opts.visitLimit, 'visit', tOr('tool.route.LimitSpec', 'specification')],
    [opts.routeLimit, 'route', tOr('tool.route.LimitControl', 'control limit')],
  ]

  return (
    <div className="rf-plot centred">
      <div className="rf-centreline" />
      {limits.map(([limit, cls, label]) =>
        limit === undefined
          ? null
          : [1, -1].map((sign) => (
              <div
                key={`${cls}${sign}`}
                className={'rf-limit ' + cls}
                style={{ bottom: `${pos(sign * limit).toFixed(1)}%` }}
                data-label={
                  sign > 0
                    ? `${label} ±${limit.toFixed(2)}${opts.unit ? ' ' + opts.unit : ''}`
                    : ''
                }
              />
            )),
      )}
      <div className="rf-marks">
        {values.map((v, i) => (
          <div className="rf-mark" key={i}>
            <span
              className="rf-dot"
              style={{ bottom: `${pos(v - opts.centre).toFixed(1)}%` }}
              title={`${opts.labels?.[i] ?? String(i + 1)}: ${v.toFixed(2)}`}
            />
            <span className="rf-x">{opts.labels?.[i] ?? String(i + 1)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * A row of values with the two limits drawn on it.
 *
 * The point of the picture is that the bars stay under one line and go over
 * the other, which is the whole shape of a route fault in one glance.
 */
function series(values: number[], opts: SeriesOpts): ReactNode {
  if (opts.centre !== undefined) return centredSeries(values, opts as SeriesOpts & { centre: number })
  const top = Math.max(...values, opts.visitLimit ?? 0, opts.routeLimit ?? 0) * 1.1
  return (
    <div className="rf-plot">
      {values.map((v, i) => (
        <div className="rf-bar" key={i}>
          <div
            className={opts.routeLimit !== undefined && v > opts.routeLimit ? 'rf-fill over' : 'rf-fill'}
            style={{ height: `${((100 * v) / top).toFixed(1)}%` }}
            title={`${opts.labels?.[i] ?? String(i + 1)}: ${v.toFixed(1)} ${opts.unit}`}
          />
          <span className="rf-x">{opts.labels?.[i] ?? String(i + 1)}</span>
        </div>
      ))}
      {opts.visitLimit !== undefined && (
        <div
          className="rf-limit visit"
          style={{ bottom: `${((100 * opts.visitLimit) / top).toFixed(1)}%` }}
          data-label={t('tool.route.LimitPerVisit', { limit: opts.visitLimit, unit: opts.unit })}
        />
      )}
      {opts.routeLimit !== undefined && (
        <div
          className="rf-limit route"
          style={{ bottom: `${((100 * opts.routeLimit) / top).toFixed(1)}%` }}
          data-label={t('tool.route.LimitRouteNeeds', { limit: opts.routeLimit, unit: opts.unit })}
        />
      )}
    </div>
  )
}

/** One fault case: the two verdicts, the data, and the answer on request. */
function FaultCard({
  caseId,
  chart,
  toolVerdict,
  routeVerdict,
}: {
  caseId: string
  chart: ReactNode
  toolVerdict: string
  routeVerdict: string
}) {
  useLang()
  const [shown, setShown] = useState(false)
  const c = ROUTE_FAULTS.find((x) => x.id === caseId)!

  return (
    <div className="rf-card">
      <h4 className="rf-title">{tOr(`tool.route.case.${caseId}.Label`, c.label)}</h4>
      <div className="rf-verdicts">
        <div className="rf-verdict pass">
          <span className="rf-vk">{tOr('tool.route.TextWhatTheToolSays', 'What the tool says')}</span>
          <span className="rf-vv">{toolVerdict}</span>
          <span className="rf-vn">{tOr(`tool.route.case.${caseId}.ToolSays`, c.toolSays)}</span>
        </div>
        <div className="rf-verdict fail">
          <span className="rf-vk">
            {tOr('tool.route.TextWhatTheProductSays', 'What the product says')}
          </span>
          <span className="rf-vv">{routeVerdict}</span>
          <span className="rf-vn">{tOr(`tool.route.case.${caseId}.ProductSays`, c.productSays)}</span>
        </div>
      </div>
      {chart}
      <div className="btn-row" style={{ marginTop: 10 }}>
        {!shown && (
          <button className="btn" onClick={() => setShown(true)}>
            {tOr('tool.route.TextShowWhatWasActuallyWrong', 'Show what was actually wrong')}
          </button>
        )}
      </div>
      <div className="rf-reveal">
        {shown && (
          <>
            <div className="rf-truth">
              <span className="rf-vk">{tOr('tool.route.TextTheFault', 'The fault')}</span>
              <span className="rf-vn">{tOr(`tool.route.case.${caseId}.Truth`, c.truth)}</span>
            </div>
            <div className="rf-truth how">
              <span className="rf-vk">
                {tOr('tool.route.TextHowYouWouldSeeIt', 'How you would see it')}
              </span>
              <span className="rf-vn">{tOr(`tool.route.case.${caseId}.HowToSeeIt`, c.howToSeeIt)}</span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Faults that only exist on a route.
 *
 * Kept in this view rather than in a tool panel for the reason that makes
 * them worth teaching: there is no tool to open. Every tool involved
 * passes every check it has.
 */
function RouteFaultsView({
  journal,
  shift,
}: {
  journal: Journal | null
  shift: ShiftState<RouteAspect, RouteCondition> | null
}) {
  useLang()
  const worn = planarisationStack(10, 0.55)
  const healthy = planarisationStack(10, NOMINAL_PLANARISATION_EFFICIENCY)
  const stretched = thermalAccount([
    { label: tOr('tool.route.LabelWellDriveIn', 'Well drive-in'), seconds: 880 },
    { label: tOr('tool.route.LabelGateOxide', 'Gate oxide'), seconds: 240 },
    { label: tOr('tool.route.LabelDensify', 'Densify'), seconds: 120 },
    { label: tOr('tool.route.LabelSpike', 'Spike'), seconds: 60 },
  ])
  const drifting = driftAcrossLayers({
    layers: 11,
    target: 0,
    driftPerLayer: 0.18,
    noiseSigma: 0.35,
    controlSigma: 0.9,
    specHalfWidth: 3,
  })

  return (
    <div>
      <div className="route-purposeline">
        {tOr(
          'tool.route.FaultIntro',
          'Every fault elsewhere in this simulator is a tool fault: something is wrong with a ' +
            'chamber and the chamber says so. These three are not. In each one every single visit ' +
            'is inside its specification and the product is outside its own — so there is no bad ' +
            'reading to find, no alarm to answer and no tool to fix. The competency is to stop ' +
            'looking at the tool, which is the hardest habit to teach because every instinct a ' +
            'good technician has says to go and check the tool.',
        )}
      </div>

      {/* The exercise. Everything below it is reference material for after. */}
      {journal && shift && (
        <>
          <h4 className="route-h">{tOr('tool.route.TextTakeTheCall', 'Take the call')}</h4>
          <Note>
            {tOr(
              'tool.route.NoteTakeTheCall',
              'Four candidate causes, and one of them is that it is simply one tool. The station ' +
                'alarms are free and the layer plots are not, which is the decision: an hour and a ' +
                'half of tool time to see a trend, against calling it from a summary that looks ' +
                'green either way.',
            )}
          </Note>
          <ShiftReportPane shift={shift} journal={journal} />
          <h4 className="route-h" style={{ marginTop: 22 }}>
            {tOr('tool.route.TextTheThreeMechanisms', 'The three mechanisms, worked through')}
          </h4>
        </>
      )}

      <FaultCard
        caseId="planarisation-drift"
        chart={series(worn.visits.map((v) => v.outgoingNm), {
          visitLimit: CMP_VISIT_SPEC_NM,
          routeLimit: CMP_ROUTE_DERIVED_SPEC_NM,
          unit: 'nm',
          labels: worn.visits.map((v) => String(v.layer)),
        })}
        toolVerdict={tOr('tool.route.VerdictInSpecAll', 'in spec on all 10 layers')}
        routeVerdict={t('tool.route.VerdictCdSpread', {
          spread: worn.cdSpreadNm.toFixed(1),
          budget: CD_BUDGET_NM,
        })}
      />
      <div className="rf-aside">
        {t('tool.route.AsidePlanarisation', {
          healthy: healthy.finalRangeNm.toFixed(1),
          worn: worn.finalRangeNm.toFixed(1),
          visit: CMP_VISIT_SPEC_NM,
          route: CMP_ROUTE_DERIVED_SPEC_NM,
        })}
      </div>

      <FaultCard
        caseId="thermal-account"
        chart={series(stretched.visits.map((v) => v.junctionNm), {
          routeLimit: stretched.junctionLimitNm,
          unit: 'nm',
          labels: stretched.visits.map((v) => v.label.split(' ')[0]),
        })}
        toolVerdict={tOr('tool.route.VerdictAnnealsOk', 'every anneal inside its own window')}
        routeVerdict={t('tool.route.VerdictJunction', {
          junction: stretched.finalJunctionNm.toFixed(0),
          limit: stretched.junctionLimitNm,
        })}
      />

      <FaultCard
        caseId="slow-drift"
        chart={series(drifting.visits.map((v) => v.value), {
          centre: drifting.centre,
          visitLimit: drifting.specHalfWidth,
          routeLimit: 3 * drifting.sigma,
          unit: '',
          labels: drifting.visits.map((v) => String(v.layer)),
        })}
        toolVerdict={tOr('tool.route.VerdictNoReadingOut', 'no reading outside spec or control limits')}
        routeVerdict={
          drifting.violations.length
            ? [...new Set(drifting.violations.map((v) => v.rule))].join('; ')
            : tOr('tool.route.VerdictNoRule', 'no rule fired')
        }
      />
      <div className="rf-aside">
        {t('tool.route.AsideDrift', { total: drifting.totalDriftNm.toFixed(2) })}
      </div>
    </div>
  )
}

/** One knob of the route economics - not recipe-shaped, so not a KnobBank. */
function Knob({
  label,
  value,
  min,
  max,
  step,
  fmt,
  onChange,
}: {
  label: string
  value: number
  min: number
  max: number
  step: number
  fmt: (v: number) => string
  onChange: (v: number) => void
}) {
  return (
    <label className="route-knob">
      <span className="route-knoblabel">{label}</span>
      <Slider
        min={min}
        max={max}
        step={step}
        value={value}
        tooltip={{ open: false }}
        style={{ width: 130, margin: '0 6px' }}
        onChange={onChange}
      />
      <span className="route-knobval">{fmt(value)}</span>
    </label>
  )
}

/**
 * Where the explorer stood when it was last used, remembered per machine.
 * The overlay is rebuilt on every open, and a student halfway through the
 * fault exercise who closes it to go look at a tool should come back to the
 * fault exercise, not to the comparison table. Only the nav selection is
 * kept - the knobs reset, because a forgotten die size silently changing
 * every number is worse than moving a slider again.
 */
const VIEW_KEY = 'simfab.routes.view'

interface Selection {
  view: 'compare' | 'faults' | 'flow'
  selected: ProcessFlow | null
}

function initialSelection(): Selection {
  try {
    const saved = localStorage.getItem(VIEW_KEY)
    if (saved === 'faults') return { view: 'faults', selected: null }
    if (saved?.startsWith('flow:')) {
      const flow = CATALOGUE.flatMap((g) => g.flows).find((f) => f.id === saved.slice(5))
      if (flow) return { view: 'flow', selected: flow }
    }
  } catch {
    // Storage blocked - the explorer just opens on the comparison.
  }
  return { view: 'compare', selected: null }
}

export function RouteExplorerView({ journal }: { journal: Journal | null }) {
  useLang()
  const [options, setOptions] = useState<RouteOptions>(() => ({ ...DEFAULT_ROUTE_OPTIONS }))
  const [sel, setSel] = useState<Selection>(initialSelection)

  /**
   * Kept across re-renders, so switching to another route view and back does
   * not silently hand the student a new shift and lose the one they were
   * halfway through. Per open, which is what the old overlay was.
   */
  const [shift] = useState<ShiftState<RouteAspect, RouteCondition> | null>(() =>
    journal ? new ShiftState(ROUTE_CONDITION_MODEL, { journal }) : null,
  )

  const select = (next: Selection): void => {
    setSel(next)
    try {
      localStorage.setItem(
        VIEW_KEY,
        next.view === 'flow' && next.selected ? `flow:${next.selected.id}` : next.view,
      )
    } catch {
      // Storage blocked - nothing to remember into.
    }
  }

  return (
    <div className="route-explorer">
      <div className="route-side">
        <h3>{t('routes.Button')}</h3>
        <p className="route-side-note">
          {tOr(
            'tool.route.SideNote',
            'A fab is not a line of tools. It is a loop, run once per layer, and a product is ' +
              'the order in which a wafer goes round it.',
          )}
        </p>
        <div className="route-nav">
          <button
            className={sel.view === 'compare' ? 'route-navitem active' : 'route-navitem'}
            onClick={() => select({ view: 'compare', selected: null })}
          >
            {tOr('tool.route.TextComparePatterning', 'Compare patterning')}
          </button>
          <button
            className={sel.view === 'faults' ? 'route-navitem active' : 'route-navitem'}
            onClick={() => select({ view: 'faults', selected: null })}
          >
            {tOr('tool.route.TextRouteFaults', 'Route faults')}
          </button>
          {CATALOGUE.map(({ group, groupId, flows }) => (
            <Fragment key={groupId}>
              <div className="route-navgroup">{tOr(`tool.route.group.${groupId}`, group)}</div>
              {flows.map((f) => (
                <button
                  key={f.id}
                  className={
                    sel.view === 'flow' && sel.selected?.id === f.id
                      ? 'route-navitem active'
                      : 'route-navitem'
                  }
                  onClick={() => select({ view: 'flow', selected: f })}
                >
                  {tOr(`tool.route.flow.${f.id}.Name`, f.name)}
                </button>
              ))}
            </Fragment>
          ))}
        </div>
      </div>
      <div className="route-main">
        <div className="route-controls">
          <Knob
            label={tOr('tool.route.KnobDieArea', 'Die area')}
            value={options.dieAreaCm2}
            min={0.05}
            max={8}
            step={0.05}
            fmt={(v) => v.toFixed(2) + ' cm²'}
            onChange={(v) => setOptions((o) => ({ ...o, dieAreaCm2: v }))}
          />
          <Knob
            label={tOr('tool.route.KnobVolume', 'Production volume')}
            value={options.waferVolume}
            min={1000}
            max={500_000}
            step={1000}
            fmt={(v) => (v >= 1000 ? t('tool.route.KnobWafers', { n: (v / 1000).toFixed(0) }) : String(v))}
            onChange={(v) => setOptions((o) => ({ ...o, waferVolume: v }))}
          />
        </div>
        <div className="route-body">
          {sel.view === 'faults' ? (
            <RouteFaultsView journal={journal} shift={shift} />
          ) : sel.view === 'flow' && sel.selected ? (
            <RouteView flow={sel.selected} options={options} />
          ) : (
            <ComparisonView options={options} />
          )}
        </div>
      </div>
    </div>
  )
}
